import { useState, useRef, useEffect, useCallback } from "react";
import { Tag, Button, Modal, Dropdown } from "antd";
import {
  EditOutlined,
  SafetyCertificateOutlined,
  PrinterOutlined,
  BankOutlined,
  ExclamationCircleOutlined,
  DownOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import ToolBarCustom from "src/components/ToolBarCustom";
import { COMPANY_DEFAULTS } from "./constants/companyDefaults";
import CollaboratorContractPreview from "./templates/CollaboratorContractPreview";
import LaborContractPreview from "./templates/LaborContractPreview";
import ContractTable from "./components/ContractTable";
import ContractForm from "./components/ContractForm";
import { css } from "./style/styles";
import CompanyModal from "./Modal/CompanyModal";
import DeleteConfirm from "./components/DeleteConfirm";
import { useContracts } from "./hooks/useContracts";
import Filter from "./components/Filter";
import Stats from "./components/Stats";
import CustomModal from "src/components/Modal/CustomModal";
import ImportModal from "./Modal/ImportModal";
import SignatureModal from "./components/SignatureModal";
import OtpModal from "./components/OtpModal";
import ManagerSignModal from "./components/ManagerSignModal";
import STORAGE, { getStorage } from "src/lib/storage";
import notice from "src/components/Notice";

const hasAnyContractSignature = (contract) =>
  !!(
    contract?.employeeSignature ||
    contract?.employee_signature ||
    contract?.managerSignature ||
    contract?.manager_signature
  );

export default function ContractManager() {
  const [tab, setTab] = useState("list");
  const [activeType, setActiveType] = useState("labor");
  const [formData, setFormData] = useState({});
  const [company, setCompany] = useState(() => {
    try {
      const saved = localStorage.getItem("COMPANY_CONFIG_CONTRACT");
      if (saved) {
        const savedCompany = JSON.parse(saved);
        if (savedCompany.sealPin || savedCompany.stampImage) {
          const sanitizedCompany = { ...savedCompany };
          delete sanitizedCompany.sealPin;
          delete sanitizedCompany.stampImage;
          localStorage.setItem("COMPANY_CONFIG_CONTRACT", JSON.stringify(sanitizedCompany));
        }
        return savedCompany;
      }
    } catch (e) {}
    return COMPANY_DEFAULTS;
  });
  const [editingId, setEditingId] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isModalImport, setIsModalImport] = useState(false);

  // State cho ký điện tử & OTP
  const [showEmployeeSigModal, setShowEmployeeSigModal] = useState(false);
  const [showEmployeeOtpModal, setShowEmployeeOtpModal] = useState(false);
  const [pendingEmployeeSignature, setPendingEmployeeSignature] = useState(null);
  const [showManagerSignModal, setShowManagerSignModal] = useState(false);
  const [showSignOrderWarningModal, setShowSignOrderWarningModal] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const printRef = useRef();
  const inputSearchRef = useRef("");

  const {
    contracts,
    saveContract,
    deleteContract,
    terminateContract,
    handleUploadFiles,
    fetchDetail,
    previewContract,
    fetchContracts,
    loading,
    params,
    setParams,
    fetchAllPersonel,
    listPersponel,
    provinces,
    fetchProvinces,
    saveContractSignature,
    signatoryConfig,
    fetchSignatoryConfig,
    saveSignatoryConfig,
  } = useContracts();

  const userInfo = getStorage(STORAGE.USER_INFO) || {};
  const permissions = localStorage.getItem(STORAGE.PERMISSIONS) || getStorage(STORAGE.PERMISSIONS) || "";
  const storedRoles = localStorage.getItem(STORAGE.ROLES) || getStorage(STORAGE.ROLES) || "";
  const roleStr = typeof storedRoles === "string" ? storedRoles : JSON.stringify(storedRoles);
  const userRole = userInfo?.positions?.role || userInfo?.role || "";
  const isAdmin =
    roleStr === "ADMIN_ROLE" ||
    roleStr.includes("ADMIN") ||
    userRole === "ADMIN_ROLE" ||
    String(userRole).includes("ADMIN") ||
    userInfo?.is_admin === "yes" ||
    userInfo?.is_admin === 1 ||
    userInfo?.is_admin === "1" ||
    userInfo?.is_admin === true;

  const checkPermission = useCallback((key) => {
    if (isAdmin) return true;
    if (Array.isArray(permissions)) return permissions.includes(key);
    return typeof permissions === "string" && permissions.includes(key);
  }, [isAdmin, permissions]);

  // Quyền ký duyệt Bên A (Sếp / Công ty)
  const canSignAsManager = isAdmin || checkPermission("contract.manager_sign") || checkPermission("contract.stamp");


  useEffect(() => {
    fetchAllPersonel();
    fetchProvinces();
    if (canSignAsManager) fetchSignatoryConfig();
  }, [fetchAllPersonel, fetchProvinces, canSignAsManager, fetchSignatoryConfig]);

  useEffect(() => {
    if (checkPermission("contract.list")) {
      fetchContracts(params);
    }
  }, [params, fetchContracts, checkPermission]);

  const onSearch = () => {
    setParams((prev) => ({ ...prev, page: 1, keyword: inputSearchRef.current }));
  };

  const openNew = () => {
    if (!checkPermission("contract.create")) {
      notice({
        isSuccess: false,
        msg: "Bạn không có quyền thực hiện chức năng tạo mới hợp đồng!",
      });
      return;
    }
    setFormData({
      position: "Nhân viên kinh doanh",
      desc: "Tìm kiếm, phát triển mạng lưới đại lý khách hàng kiểm tra và xử lý",
    });
    setEditingId(null);
    setActiveType("labor");
    setTab("form");
  };

  const openEdit = async (c) => {
    if (!checkPermission("contract.update")) {
      notice({
        isSuccess: false,
        msg: "Bạn không có quyền thực hiện chức năng chỉnh sửa hợp đồng!",
      });
      return;
    }

    const contractId = c.id || c.ID;
    setEditingId(contractId);

    const detailLoaded = await fetchDetail(contractId);
    const contractData = detailLoaded || c;

    if (!detailLoaded) {
      notice({
        isSuccess: false,
        msg: "Không thể xác minh trạng thái chữ ký của hợp đồng. Vui lòng thử lại!",
      });
      return;
    }

    if (hasAnyContractSignature(contractData)) {
      notice({
        isSuccess: false,
        msg: "Hợp đồng đã có chữ ký của một bên nên không thể chỉnh sửa!",
      });
      return;
    }

    setPreviewId(null);
    setActiveType(contractData.type || "labor");
    setTab("form");
    setFormData({
      ...contractData,
      insurance: contractData.insurance ? 1 : 0,
    });
  };

  const handleOpenPreview = (recordId) => {
    if (!checkPermission("contract.detail")) {
      notice({
        isSuccess: false,
        msg: "Bạn không có quyền thực hiện chức năng xem chi tiết hợp đồng!",
      });
      return;
    }
    setPreviewId(recordId);
  };


  const handleOpenTerminate = (record) => {
    if (!checkPermission("contract.terminate")) {
      notice({
        isSuccess: false,
        msg: "Bạn không có quyền thực hiện chức năng kết thúc hợp đồng!",
      });
      return;
    }

    const recordId = record?.id || record?.ID;
    Modal.confirm({
      title: "Xác nhận kết thúc hợp đồng",
      icon: <ExclamationCircleOutlined style={{ color: "#e11d48" }} />,
      content: (
        <div>
          <p style={{ margin: "4px 0" }}>
            Bạn có chắc chắn muốn kết thúc hợp đồng <b>{record?.code}</b> của nhân sự <b>{record?.employeeName}</b> không?
          </p>
          <p style={{ color: "#64748b", fontSize: 12, margin: 0 }}>
            Sau khi kết thúc, trạng thái hợp đồng sẽ chuyển sang <b>Đã kết thúc</b> và không thể chỉnh sửa.
          </p>
        </div>
      ),
      okText: "Kết thúc hợp đồng",
      okType: "danger",
      cancelText: "Hủy",
      onOk: async () => {
        try {
          await terminateContract(recordId);
        } catch (err) {
          console.error("Terminate contract failed:", err);
        }
      },
    });
  };

  const handleOpenDeleteConfirm = (recordId) => {
    if (!checkPermission("contract.delete")) {
      notice({
        isSuccess: false,
        msg: "Bạn không có quyền thực hiện chức năng xóa hợp đồng!",
      });
      return;
    }
    setDeleteConfirm(recordId);
  };

  const handleSaveContract = async () => {
    try {
      if (editingId) {
        const latestContract = await fetchDetail(editingId);
        if (!latestContract) {
          notice({
            isSuccess: false,
            msg: "Không thể xác minh trạng thái chữ ký của hợp đồng. Vui lòng thử lại!",
          });
          return;
        }
        if (hasAnyContractSignature(latestContract)) {
          notice({
            isSuccess: false,
            msg: "Hợp đồng đã có chữ ký của một bên nên không thể chỉnh sửa!",
          });
          setTab("list");
          setFormData({});
          setEditingId(null);
          return;
        }
      }
      await saveContract(editingId, activeType, formData);
      setTab("list");
      setFormData({});
      setEditingId(null);
    } catch (error) {
      console.error("Save contract failed:", error);
    }
  };

  const handleDeleteContract = (id) => {
    if (!checkPermission("contract.delete")) {
      notice({
        isSuccess: false,
        msg: "Bạn không có quyền thực hiện chức năng xóa hợp đồng!",
      });
      setDeleteConfirm(null);
      return;
    }
    deleteContract(id);
    setDeleteConfirm(null);
  };

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;

    const w = window.open("", "_blank");
    if (!w) return;

    const style = w.document.createElement("style");
    style.textContent = `
      body {
        margin: 0;
        padding: 0;
      }
      @media print {
        body {
          margin: 0;
        }
      }
    `;

    w.document.head.appendChild(style);
    const wrapper = w.document.createElement("div");
    wrapper.innerHTML = content.innerHTML;
    w.document.body.appendChild(wrapper);
    w.document.title = "Hợp đồng";

    w.focus();
    setTimeout(() => {
      w.print();
      w.close();
    }, 400);
  };

  useEffect(() => {
    if (!previewId) return;
    fetchDetail(previewId);
  }, [previewId, fetchDetail]);

  const handleCloseModalImport = () => {
    setIsModalImport(false);
  };

  const handleImportSuccess = () => {
    handleCloseModalImport();
    fetchContracts(params);
  };

  // --- KÝ ĐIỆN TỬ: BÊN B (NGƯỜI LAO ĐỘNG) ---
  const handleOpenEmployeeSign = () => {
    if (canSignAsManager) {
      notice({
        isSuccess: false,
        msg: "Bạn có quyền Ký duyệt & Đóng dấu (Bên A), không thể ký thay cho Người lao động (Bên B)!",
      });
      return;
    }
    if (previewContract?.employeeSignature) {
      notice({
        isSuccess: true,
        msg: "Người lao động đã ký hợp đồng này rồi! Bạn có thể xem chữ ký ở chân trang.",
      });
      return;
    }
    setShowEmployeeSigModal(true);
  };

  const handleEmployeeSignatureConfirmed = (sigDataUrl) => {
    setPendingEmployeeSignature(sigDataUrl);
    setShowEmployeeSigModal(false);
    setShowEmployeeOtpModal(true);
  };

  const handleEmployeeOtpSuccess = async ({ verifiedAt, verifiedEmail, otpCode }) => {
    const contractId = previewContract?.id || previewContract?.ID;
    const payload = {
      employeeSignature: pendingEmployeeSignature,
      employeeSignedAt: verifiedAt,
      employeeEmail: verifiedEmail,
      employeeOtpVerified: 1,
      otpCode: otpCode,
      employeeUserAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      sign_status: "waiting_manager",
    };

    await saveContractSignature(contractId, payload);
    setShowEmployeeOtpModal(false);
    notice({
      isSuccess: true,
      msg: "Người lao động đã hoàn tất ký tên & xác thực Email OTP thành công! Đã gửi thông báo đến Giám đốc để ký duyệt & đóng dấu.",
    });
  };

  // --- KÝ ĐIỆN TỬ: BÊN A (GIÁM ĐỐC / SẾP KÝ VÀ ĐÓNG DẤU ĐỎ) ---
  const handleOpenManagerSign = () => {
    if (!canSignAsManager) {
      notice({
        isSuccess: false,
        msg: "Bạn không có quyền đại diện Người sử dụng lao động để ký duyệt và đóng dấu công ty!",
      });
      return;
    }
    if (previewContract?.managerSignature) {
      notice({
        isSuccess: true,
        msg: "Giám đốc đã ký duyệt và đóng dấu mộc đỏ vào hợp đồng này rồi!",
      });
      return;
    }
    if (!signatoryConfig?.configured) {
      notice({
        isSuccess: false,
        msg: "Vui lòng cấu hình chữ ký và con dấu của Giám đốc trước khi duyệt hợp đồng!",
      });
      setShowCompanyModal(true);
      return;
    }
    if (!previewContract?.employeeSignature) {
      setShowSignOrderWarningModal(true);
      return;
    }
    setShowManagerSignModal(true);
  };

  const handleManagerSignConfirmed = async ({
    managerApprove,
    applySideStamp,
  }) => {
    const contractId = previewContract?.id || previewContract?.ID;
    let docHash = "";
    try {
      docHash = Array.from(crypto.getRandomValues(new Uint8Array(32)))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    } catch (e) {
      docHash = "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855" + Date.now().toString(16);
    }

    const payload = {
      managerApprove,
      stampApplied: 1,
      applySideStamp,
      documentHash: docHash,
    };

    const saved = await saveContractSignature(contractId, payload);
    if (!saved) return;
    setShowManagerSignModal(false);
    notice({
      isSuccess: true,
      msg: "Giám đốc đã ký duyệt và đóng dấu mộc đỏ công ty thành công! Hợp đồng đã có đầy đủ hiệu lực pháp lý.",
    });
  };

  // --- TIỆN ÍCH IN VÀ XUẤT FILE PDF RIÊNG BIỆT ---
  const sanitizeFileName = (str) => {
    if (!str) return "";
    return str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[đĐ]/g, (m) => (m === "đ" ? "d" : "D"))
      .replace(/[^a-zA-Z0-9_-]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
  };

  // Helper nhúng ảnh thành base64 để tránh lỗi CORS và đảm bảo html2canvas / print luôn hiển thị đầy đủ 100% con dấu và chữ ký
  const inlineImagesAsBase64 = async (rootEl) => {
    if (!rootEl) return;
    const imgs = Array.from(rootEl.querySelectorAll("img"));
    await Promise.all(
      imgs.map(async (img) => {
        const src = img.getAttribute("src") || img.src;
        if (!src || src.startsWith("data:")) return;
        try {
          const resp = await fetch(src, { mode: "cors" });
          if (!resp.ok) return;
          const blob = await resp.blob();
          await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              if (reader.result) {
                img.src = reader.result;
              }
              resolve();
            };
            reader.onerror = resolve;
            reader.readAsDataURL(blob);
          });
        } catch (e) {
          console.warn("Could not inline image as base64:", src, e);
        }
      })
    );
  };

  // 1. IN TÀI LIỆU (Tách riêng Hợp đồng và Biên bản chứng nhận điện tử)
  const handlePrintDocument = async (docType = "all") => {
    if (!printRef.current) return;

    const isCollaborator = previewContract?.type === "collaborator";
    const fileCode = sanitizeFileName(previewContract?.code || "HD");
    const empName = sanitizeFileName(previewContract?.employeeName || previewContract?.staff_name || "Nhan_Vien");

    let targetEl = null;
    let title = "Tai_Lieu";

    if (docType === "main") {
      targetEl = printRef.current.querySelector("#contract-main-doc");
      title = `${isCollaborator ? "Hop_Dong_Cong_Tac_Vien" : "Hop_Dong_Lao_Dong"}_${fileCode}_${empName}`;
    } else if (docType === "audit") {
      targetEl = printRef.current.querySelector("#contract-audit-doc");
      title = `Bien_Ban_Chung_Nhan_Giao_Ket_${fileCode}_${empName}`;
    } else {
      targetEl = printRef.current;
      title = `Tron_Bo_Hop_Dong_Va_Chung_Nhan_${fileCode}_${empName}`;
    }

    if (!targetEl) {
      notice({
        isSuccess: false,
        msg: "Chưa có nội dung tài liệu này để in (hoặc hợp đồng chưa có chữ ký để cấp biên bản chứng nhận)!",
      });
      return;
    }

    const clone = targetEl.cloneNode(true);
    await inlineImagesAsBase64(clone);

    if (clone.classList?.contains("contract-preview-container")) {
      clone.style.padding = "0px";
    } else {
      const innerContainer = clone.querySelector(".contract-preview-container");
      if (innerContainer) {
        innerContainer.style.padding = "0px";
      }
    }

    if (docType === "audit") {
      const auditEl = clone.querySelector(".audit-trail-page") || clone;
      if (auditEl) {
        auditEl.style.pageBreakBefore = "avoid";
        auditEl.style.marginTop = "0px";
        auditEl.style.borderTop = "none";
        auditEl.style.paddingTop = "10px";
      }
    }

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html>
<html>
<head>
  <title>${title}</title>
  <meta charset="utf-8" />
  <style>
    @page {
      size: A4 portrait;
      margin: 15mm 15mm 15mm 15mm;
    }
    * {
      box-sizing: border-box;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #111;
      font-family: 'Times New Roman', Times, serif;
      font-size: 13.5px;
      line-height: 1.55;
    }
    .contract-preview-container {
      padding: 0 !important;
      background: #fff !important;
    }
    table {
      border-collapse: collapse;
      width: 100%;
    }
    .contract-page-break {
      border-top: none !important;
      margin-top: 0 !important;
      padding-top: 0 !important;
    }
    .contract-page-section {
      position: relative !important;
    }
    .contract-giap-lai-stamp {
      display: block !important;
      position: absolute !important;
      right: -24px !important;
      top: 48% !important;
      transform: translateY(-50%) rotate(-6deg) !important;
      width: 86px !important;
      height: 86px !important;
      z-index: 20 !important;
      pointer-events: none !important;
      opacity: 0.92 !important;
    }
    ${docType === "audit" ? `
    .contract-giap-lai-stamp {
      display: none !important;
    }
    ` : ""}
    ${docType === "audit" ? `
    .audit-trail-page {
      page-break-before: avoid !important;
      margin-top: 0 !important;
      border-top: none !important;
      padding-top: 0 !important;
    }
    ` : ""}
  </style>
</head>
<body></body>
</html>`);
    doc.close();
    doc.body.appendChild(clone);

    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 1500);
    }, 400);
  };

  // 2. XUẤT FILE PDF (Tách riêng file Hợp đồng và file Biên bản chứng nhận giao kết điện tử)
  const handleExportPdf = async (docType = "all") => {
    if (!printRef.current) return;

    const isCollaborator = previewContract?.type === "collaborator";
    const fileCode = sanitizeFileName(previewContract?.code || "HD");
    const empName = sanitizeFileName(previewContract?.employeeName || previewContract?.staff_name || "Nhan_Vien");

    let targetEl = null;
    let filename = "";

    if (docType === "main") {
      targetEl = printRef.current.querySelector("#contract-main-doc");
      filename = `${isCollaborator ? "Hop_Dong_Cong_Tac_Vien" : "Hop_Dong_Lao_Dong"}_${fileCode}_${empName}.pdf`;
    } else if (docType === "audit") {
      targetEl = printRef.current.querySelector("#contract-audit-doc");
      filename = `Bien_Ban_Chung_Nhan_Giao_Ket_${fileCode}_${empName}.pdf`;
    } else {
      targetEl = printRef.current;
      filename = `Tron_Bo_Hop_Dong_Va_Chung_Nhan_${fileCode}_${empName}.pdf`;
    }

    if (!targetEl) {
      notice({
        isSuccess: false,
        msg: "Chưa có nội dung tài liệu này để xuất PDF (hoặc hợp đồng chưa có chữ ký để cấp biên bản chứng nhận)!",
      });
      return;
    }

    setIsExportingPdf(true);
    notice({
      isSuccess: true,
      msg: `Đang khởi tạo và xuất file PDF (${filename})...`,
    });

    try {
      if (typeof window !== "undefined" && !window.self) {
        window.self = window;
      }
      const html2pdfModule = await import("html2pdf.js");
      const html2pdf = html2pdfModule.default || html2pdfModule;

      const clone = targetEl.cloneNode(true);
      await inlineImagesAsBase64(clone);

      clone.style.width = "750px";
      clone.style.boxSizing = "border-box";
      clone.style.margin = "0 auto";
      clone.style.background = "#fff";
      clone.style.fontFamily = "'Times New Roman', Times, serif";
      clone.style.fontSize = "13.5px";
      clone.style.lineHeight = "1.55";
      clone.style.padding = "24px 30px";

      const innerContainer = clone.querySelector(".contract-preview-container");
      if (innerContainer) {
        innerContainer.style.padding = "0px";
      }

      if (docType === "audit") {
        const auditEl = clone.querySelector(".audit-trail-page") || clone;
        if (auditEl) {
          auditEl.style.pageBreakBefore = "avoid";
          auditEl.style.marginTop = "0px";
          auditEl.style.borderTop = "none";
          auditEl.style.paddingTop = "10px";
        }
        clone.querySelectorAll(".contract-giap-lai-stamp").forEach((el) => el.remove());
      } else {
        clone.querySelectorAll(".contract-giap-lai-stamp").forEach((el) => {
          el.style.display = "block";
          el.style.position = "absolute";
          el.style.right = "-24px";
          el.style.top = "48%";
          el.style.transform = "translateY(-50%) rotate(-6deg)";
          el.style.width = "84px";
          el.style.height = "84px";
          el.style.zIndex = "25";
        });
      }

      clone.querySelectorAll(".contract-page-break").forEach((el) => {
        el.style.borderTop = "none";
        el.style.marginTop = "0px";
        el.style.paddingTop = "0px";
      });

      const tempContainer = document.createElement("div");
      tempContainer.style.position = "absolute";
      tempContainer.style.left = "-9999px";
      tempContainer.style.top = "0";
      tempContainer.style.width = "800px";
      tempContainer.style.background = "#fff";
      tempContainer.appendChild(clone);
      document.body.appendChild(tempContainer);

      const opt = {
        margin: [10, 8, 10, 8],
        filename: filename,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true,
          logging: false,
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
        },
        pagebreak: { mode: ["css", "legacy"] },
      };

      // Lấy trực tiếp đối tượng jsPDF từ html2pdf qua callback để trích xuất Blob chuẩn
      let realPdf = null;
      await html2pdf()
        .set(opt)
        .from(clone)
        .toPdf()
        .get("pdf", function (pdf) {
          realPdf = pdf;
        });

      if (!realPdf || typeof realPdf.output !== "function") {
        throw new Error("Không thể khởi tạo đối tượng PDF");
      }

      const pdfBlob = realPdf.output("blob");
      console.log("PDF Blob created successfully:", pdfBlob, "size:", pdfBlob?.size);

      // Tạo File / Blob với MIME type và kích hoạt tải về qua DOM
      const fileToDownload = new File([pdfBlob], filename, { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(fileToDownload);

      const downloadLink = document.createElement("a");
      downloadLink.style.display = "none";
      downloadLink.href = blobUrl;
      downloadLink.download = filename;
      downloadLink.setAttribute("download", filename);
      document.body.appendChild(downloadLink);

      downloadLink.click();

      setTimeout(() => {
        if (document.body.contains(downloadLink)) {
          document.body.removeChild(downloadLink);
        }
        window.URL.revokeObjectURL(blobUrl);
      }, 3000);

      if (document.body.contains(tempContainer)) {
        document.body.removeChild(tempContainer);
      }

      notice({
        isSuccess: true,
        msg: `Đã xuất file PDF thành công: ${filename}`,
      });
    } catch (error) {
      console.error("Lỗi xuất PDF:", error);
      notice({
        isSuccess: false,
        msg: "Có lỗi khi xuất PDF tự động. Bạn cũng có thể dùng nút In tài liệu -> Chọn 'Lưu dưới dạng PDF' của trình duyệt!",
      });
    } finally {
      setIsExportingPdf(false);
    }
  };

  const isCollaboratorDoc = previewContract?.type === "collaborator";
  const mainDocTitle = isCollaboratorDoc ? "HỢP ĐỒNG CỘNG TÁC VIÊN" : "HỢP ĐỒNG LAO ĐỘNG";

  const printMenuItems = [
    {
      key: "main",
      icon: <FileTextOutlined style={{ color: "#1677ff", fontSize: 14 }} />,
      label: (
        <span>
          In riêng: <b>{mainDocTitle}</b>
        </span>
      ),
      onClick: () => handlePrintDocument("main"),
    },
    {
      key: "audit",
      icon: <SafetyCertificateOutlined style={{ color: "#16a34a", fontSize: 14 }} />,
      label: (
        <span>
          In riêng: <b>BIÊN BẢN CHỨNG NHẬN GIAO KẾT ĐIỆN TỬ</b>
        </span>
      ),
      onClick: () => handlePrintDocument("audit"),
    },
    {
      type: "divider",
    },
    {
      key: "all",
      icon: <PrinterOutlined style={{ color: "#475569", fontSize: 14 }} />,
      label: "In trọn bộ (Hợp đồng + Biên bản chứng nhận)",
      onClick: () => handlePrintDocument("all"),
    },
  ];

  const exportPdfMenuItems = [
    {
      key: "main",
      icon: <FilePdfOutlined style={{ color: "#dc2626", fontSize: 14 }} />,
      label: (
        <span>
          Xuất PDF: <b>{mainDocTitle}</b>
        </span>
      ),
      onClick: () => handleExportPdf("main"),
    },
    {
      key: "audit",
      icon: <SafetyCertificateOutlined style={{ color: "#16a34a", fontSize: 14 }} />,
      label: (
        <span>
          Xuất PDF: <b>BIÊN BẢN CHỨNG NHẬN GIAO KẾT ĐIỆN TỬ</b>
        </span>
      ),
      onClick: () => handleExportPdf("audit"),
    },
    {
      type: "divider",
    },
    {
      key: "all",
      icon: <DownloadOutlined style={{ color: "#dc2626", fontSize: 14 }} />,
      label: "Xuất PDF trọn bộ (Hợp đồng + Biên bản)",
      onClick: () => handleExportPdf("all"),
    },
  ];

  return (
    <div>
      {/* MAIN */}
      <div style={css.main}>
        {tab === "list" && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <ToolBarCustom
                  title={canSignAsManager ? "Danh sách hợp đồng lao động & cộng tác viên" : "Hợp đồng lao động của tôi"}
                  onCreateNew={checkPermission("contract.create") ? openNew : null}
                />
              </div>
              {canSignAsManager && (
                <Button
                  icon={<BankOutlined style={{ color: "#dc2626" }} />}
                  style={{ fontWeight: 600, borderRadius: 8, height: 36, marginLeft: 16, borderColor: "#cbd5e1" }}
                  onClick={() => setShowCompanyModal(true)}
                >
                  🏢 Cấu hình Con dấu đỏ & Pháp nhân
                </Button>
              )}
            </div>
            {/* Stats */}
            <Stats contracts={contracts} />

            <Filter
              onSearch={onSearch}
              inputSearchRef={inputSearchRef}
              setIsModalImport={setIsModalImport}
              params={params}
              setParams={setParams}
              listPersponel={listPersponel}
              isAdmin={canSignAsManager}
            />

            {/* Table */}
            <ContractTable
              contracts={contracts}
              loading={loading} 
              setPreviewId={handleOpenPreview}
              setTab={setTab}
              setActiveType={setActiveType}
              openEdit={openEdit}
              setDeleteConfirm={handleOpenDeleteConfirm}
              onOpenTerminate={handleOpenTerminate}
              setShowCompanyModal={setShowCompanyModal}
              setParams={setParams}
              params={params}
              isAdmin={canSignAsManager}
              checkPermission={checkPermission}
            />
          </>
        )}

        {tab === "form" && (
          <ContractForm
            formData={formData}
            setFormData={setFormData}
            css={css}
            activeType={activeType}
            setActiveType={setActiveType}
            saveContract={handleSaveContract}
            setTab={setTab}
            setShowCompanyModal={setShowCompanyModal}
            company={company}
            editingId={editingId}
            listPersponel={listPersponel}
            fetchAllPersonel={fetchAllPersonel}
            provinces={provinces}
          />
        )}
      </div>

      {/* PREVIEW CONTRACT & DIGITAL SIGNATURE MODAL */}
      {previewId && previewContract && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            overflowY: "auto",
            padding: "20px 0",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setPreviewId(null);
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 14,
              width: "min(920px, 96vw)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px 24px",
                borderBottom: "1px solid #e2e8f0",
                background: "#f8fafc",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, display: "flex", alignItems: "center", gap: 10 }}>
                  📄 Xem chi tiết Hợp đồng — {previewContract.employeeName} {previewContract.code ? `(${previewContract.code})` : ""}
                  {/* Badge trạng thái ký */}
                  {previewContract.employeeSignature && previewContract.managerSignature ? (
                    <Tag color="success" style={{ fontWeight: 700, padding: "3px 10px", borderRadius: 16 }}>
                      🟢 ĐÃ CÓ HIỆU LỰC PHÁP LÝ
                    </Tag>
                  ) : previewContract.employeeSignature ? (
                    <Tag color="purple" style={{ fontWeight: 700, padding: "3px 10px", borderRadius: 16 }}>
                      🟣 CHỜ SẾP DUYỆT & ĐÓNG DẤU
                    </Tag>
                  ) : (
                    <Tag color="orange" style={{ fontWeight: 700, padding: "3px 10px", borderRadius: 16 }}>
                      🟠 CHỜ NHÂN VIÊN KÝ (OTP)
                    </Tag>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  Mã HĐ: <b>{previewContract.code || "—"}</b> • Loại: {previewContract.type === "labor" ? "👔 HĐ Lao động" : "🤝 HĐ Cộng tác viên"}
                </div>
              </div>

              {/* Nút hành động */}
              {/* Nút hành động phân quyền */}
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                {/* 1. Trạng thái hoặc Nút ký Bên B (Người lao động) */}
                {canSignAsManager ? (
                  // KHI CÓ QUYỀN KÝ DUYỆT & ĐÓNG DẤU: ẨN NÚT KÝ BÊN B, HIỂN THỊ BADGE TRẠNG THÁI
                  previewContract.employeeSignature ? (
                    <Tag
                      color="success"
                      style={{
                        padding: "5px 12px",
                        fontSize: 12.5,
                        fontWeight: 700,
                        borderRadius: 6,
                        margin: 0,
                      }}
                    >
                      ✓ Bên B Đã Ký OTP
                    </Tag>
                  ) : (
                    <Tag
                      color="orange"
                      style={{
                        padding: "5px 12px",
                        fontSize: 12.5,
                        fontWeight: 700,
                        borderRadius: 6,
                        margin: 0,
                      }}
                    >
                      ⏳ Bên B Chưa Ký
                    </Tag>
                  )
                ) : (
                  // KHI LÀ NHÂN VIÊN (KHÔNG CÓ QUYỀN SẾP): HIỂN THỊ NÚT KÝ BÊN B
                  previewContract.employeeSignature ? (
                    <Tag
                      color="success"
                      style={{
                        padding: "5px 12px",
                        fontSize: 12.5,
                        fontWeight: 700,
                        borderRadius: 6,
                        margin: 0,
                      }}
                    >
                      ✓ Bạn đã ký OTP
                    </Tag>
                  ) : (
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      style={{
                        background: "#1677ff",
                        color: "#fff",
                        borderColor: "#1677ff",
                        fontWeight: 700,
                      }}
                      onClick={handleOpenEmployeeSign}
                    >
                      ✍ Ký HĐ (Nhân viên)
                    </Button>
                  )
                )}

                {/* 2. Nút Sếp Ký & Đóng dấu (Bên A) - CHỈ HIỂN THỊ KHI CÓ QUYỀN ĐẠI DIỆN CÔNG TY */}
                {canSignAsManager && (
                  <Button
                    type="primary"
                    icon={<SafetyCertificateOutlined />}
                    style={{
                      background: previewContract.managerSignature
                        ? "#f1f5f9"
                        : "#dc2626",
                      color: previewContract.managerSignature ? "#dc2626" : "#fff",
                      borderColor: previewContract.managerSignature ? "#cbd5e1" : "#dc2626",
                      fontWeight: 700,
                    }}
                    onClick={handleOpenManagerSign}
                  >
                    {previewContract.managerSignature ? "★ Đã duyệt & đóng dấu" : "★ Duyệt & đóng dấu"}
                  </Button>
                )}

                {/* 3. In tài liệu riêng biệt */}
                <Dropdown
                  menu={{ items: printMenuItems }}
                  placement="bottomRight"
                  trigger={["click"]}
                >
                  <Button icon={<PrinterOutlined />} style={{ fontWeight: 600 }}>
                    In tài liệu <DownOutlined style={{ fontSize: 10, marginLeft: 2 }} />
                  </Button>
                </Dropdown>

                {/* 4. Xuất file PDF riêng biệt */}
                <Dropdown
                  menu={{ items: exportPdfMenuItems }}
                  placement="bottomRight"
                  trigger={["click"]}
                >
                  <Button
                    type="primary"
                    ghost
                    icon={<FilePdfOutlined />}
                    loading={isExportingPdf}
                    style={{
                      fontWeight: 600,
                      color: "#dc2626",
                      borderColor: "#dc2626",
                    }}
                  >
                    Xuất file PDF <DownOutlined style={{ fontSize: 10, marginLeft: 2 }} />
                  </Button>
                </Dropdown>

                {/* 4. Sửa hợp đồng - CHỈ HIỂN THỊ KHI CÓ QUYỀN VÀ CHƯA KÝ ĐÓNG DẤU */}
                {checkPermission("contract.update") && !hasAnyContractSignature(previewContract) && previewContract?.status !== "terminated" && (
                  <Button onClick={() => openEdit(previewContract)}>
                    Sửa
                  </Button>
                )}

                <Button type="text" onClick={() => setPreviewId(null)} style={{ fontWeight: "bold" }}>
                  ✕
                </Button>
              </div>
            </div>

            {/* Banner trạng thái & hướng dẫn quy trình ký theo phân quyền */}
            <div
              style={{
                padding: "12px 24px",
                background:
                  previewContract.employeeSignature && previewContract.managerSignature
                    ? "#f0fdf4"
                    : previewContract.employeeSignature
                    ? "#faf5ff"
                    : "#fffbeb",
                borderBottom: "1px solid #e2e8f0",
                fontSize: 12.5,
              }}
            >
              {previewContract.employeeSignature && previewContract.managerSignature ? (
                <div style={{ color: "#166534", fontWeight: 600 }}>
                  🔒 Hợp đồng đã hoàn tất giao kết điện tử 2 bên và niêm phong tính toàn vẹn (SHA-256). Có đầy đủ giá trị pháp lý thi hành theo Điều 14 Bộ luật Lao động và Luật Giao dịch điện tử.
                </div>
              ) : !previewContract.employeeSignature ? (
                canSignAsManager ? (
                  <div style={{ color: "#b45309", fontWeight: 600 }}>
                    ⏳ Người lao động ({previewContract.employeeName}) chưa ký xác nhận. Hệ thống khuyến nghị chờ Nhân viên ký OTP trước khi Giám đốc ký duyệt & đóng dấu.
                  </div>
                ) : (
                  <div style={{ color: "#92400e", fontWeight: 600 }}>
                    👉 <b>Quyền của bạn:</b> Vui lòng bấm vào nút <b>"✍ Ký HĐ (Nhân viên)"</b> ở góc trên bên phải để ký tên và nhận mã OTP xác thực danh tính gửi về Email.
                  </div>
                )
              ) : (
                canSignAsManager ? (
                  <div style={{ color: "#6b21a8", fontWeight: 600 }}>
                    ✓ Người lao động ({previewContract.employeeName}) đã ký & xác thực OTP qua Email. Vui lòng bấm <b>"★ Duyệt & đóng dấu"</b> để hoàn tất hợp đồng.
                  </div>
                ) : (
                  <div style={{ color: "#6b21a8", fontWeight: 600 }}>
                    ✓ <b>Bạn đã hoàn tất ký nhận hợp đồng!</b> Đang chờ Người đại diện pháp luật của Công ty ký duyệt và đóng dấu mộc đỏ để hợp đồng chính thức có hiệu lực.
                  </div>
                )
              )}
            </div>

            <div ref={printRef} style={{ overflow: "auto" }}>
              {previewContract.type === "labor" ? (
                <LaborContractPreview data={previewContract} company={company} signatoryConfig={signatoryConfig} />
              ) : (
                <CollaboratorContractPreview data={previewContract} company={company} signatoryConfig={signatoryConfig} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL KÝ CỦA NHÂN VIÊN */}
      {showEmployeeSigModal && (
        <SignatureModal
          open={showEmployeeSigModal}
          onCancel={() => setShowEmployeeSigModal(false)}
          onConfirm={handleEmployeeSignatureConfirmed}
          title="Người lao động ký chữ ký điện tử"
          signerName={previewContract?.employeeName}
          signerRole="Người lao động (Bên B)"
        />
      )}

      {/* MODAL XÁC THỰC EMAIL OTP CỦA NHÂN VIÊN */}
      {showEmployeeOtpModal && (
        <OtpModal
          open={showEmployeeOtpModal}
          contractId={previewContract?.id || previewContract?.ID}
          onCancel={() => setShowEmployeeOtpModal(false)}
          onVerifySuccess={handleEmployeeOtpSuccess}
          signerEmail={
            previewContract?.personnel?.email ||
            previewContract?.personnel?.fast_contact_email ||
            (previewContract?.personnel_id && listPersponel?.find(p => (p.ID || p.id) === previewContract.personnel_id)?.email) ||
            (previewContract?.personnel_id && listPersponel?.find(p => (p.ID || p.id) === previewContract.personnel_id)?.fast_contact_email) ||
            (previewContract?.employeeEmail && !previewContract.employeeEmail.endsWith("@cis.com.vn") ? previewContract.employeeEmail : null) ||
            (previewContract?.employee_email && !previewContract.employee_email.endsWith("@cis.com.vn") ? previewContract.employee_email : null) ||
            previewContract?.employeeEmail ||
            `${previewContract?.phone || "nhansu"}@cis.com.vn`
          }
          signerName={previewContract?.employeeName}
          contractCode={previewContract?.code}
        />
      )}

      {/* MODAL SẾP KÝ & ĐÓNG DẤU CÔNG TY */}
      {showManagerSignModal && (
        <ManagerSignModal
          open={showManagerSignModal}
          onCancel={() => setShowManagerSignModal(false)}
          onConfirm={handleManagerSignConfirmed}
          company={company}
          signatoryConfig={signatoryConfig}
          managerName={signatoryConfig?.managerName || company?.representative || "Phạm Thu Lan"}
          managerTitle={signatoryConfig?.managerTitle || company?.repTitle || "Giám đốc"}
        />
      )}

      {/* MODAL CẢNH BÁO QUY TRÌNH KÝ KẾT (BÊN B CHƯA KÝ) */}
      {showSignOrderWarningModal && (
        <Modal
          open={showSignOrderWarningModal}
          onCancel={() => setShowSignOrderWarningModal(false)}
          footer={null}
          width={520}
          centered
          styles={{ mask: { backdropFilter: "blur(4px)" } }}
          bodyStyle={{ padding: "28px 24px" }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "#fef3c7",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 16,
                boxShadow: "0 4px 14px rgba(245, 158, 11, 0.25)",
              }}
            >
              <ExclamationCircleOutlined style={{ fontSize: 32, color: "#d97706" }} />
            </div>

            <h3 style={{ fontSize: 18, fontWeight: 800, color: "#1e293b", marginBottom: 8 }}>
              Cảnh báo quy trình ký kết
            </h3>

            <div
              style={{
                background: "#fffbeb",
                border: "1px solid #fde68a",
                borderRadius: 10,
                padding: "14px 18px",
                margin: "16px 0 20px 0",
                textAlign: "left",
              }}
            >
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <span style={{ fontSize: 16, lineHeight: 1.2 }}>⚠️</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e", marginBottom: 4 }}>
                    Người lao động (Bên B) chưa ký hợp đồng này!
                  </div>
                  <div style={{ fontSize: 12.5, color: "#b45309", lineHeight: 1.6 }}>
                    Người lao động cần ký xác nhận OTP trước. Sau đó Giám đốc có thể duyệt; chữ ký và con dấu đã cấu hình sẽ tự động được chèn vào hợp đồng.
                  </div>
                </div>
              </div>
            </div>

            <p style={{ fontSize: 13.5, color: "#475569", marginBottom: 24, lineHeight: 1.5 }}>
              Chưa thể duyệt hợp đồng cho đến khi Người lao động hoàn tất ký OTP.
            </p>

            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <Button
                size="large"
                onClick={() => setShowSignOrderWarningModal(false)}
                style={{
                  borderRadius: 8,
                  fontWeight: 600,
                  minWidth: 100,
                }}
              >
                Đóng
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* COMPANY MODAL */}
      {showCompanyModal && (
        <CompanyModal
          company={company}
          setCompany={setCompany}
          setShowCompanyModal={setShowCompanyModal}
          signatoryConfig={signatoryConfig}
          saveSignatoryConfig={saveSignatoryConfig}
        />
      )}

      {/* DELETE CONFIRM */}
      {deleteConfirm && (
        <DeleteConfirm
          deleteContract={handleDeleteContract}
          deleteConfirm={deleteConfirm}
          setDeleteConfirm={setDeleteConfirm}
        />
      )}

      {isModalImport && (
        <CustomModal
          title={"Tải file lên"}
          footer={null}
          size={"small"}
          open={isModalImport}
          onCancel={handleCloseModalImport}
        >
          <ImportModal
            open={isModalImport}
            onCancel={handleCloseModalImport}
            onOk={handleImportSuccess}
          />
        </CustomModal>
      )}
    </div>
  );
}